import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { IdParamsType } from 'utils/types';
import { PunchupAnalyticsSection } from 'features/common/components/Punchup/primitives/charts/PunchupAnalyticsSection';
import { PunchupAnalyticsEmptyState } from 'features/common/components/Punchup/primitives/charts/PunchupAnalyticsEmptyState';
import { ChartPalette } from '../PunchupAnalytics';
import { useTicketInventoryData } from './useTicketInventoryData';
import { useCompedInventory } from '../useCompedInventory';
import { useNewRowIndices } from './useNewRowIndices';
import { InventoryColumnCard, InventoryColumnCardProps } from './InventoryColumnCard';
import { buildTypeColumnProps, ColumnPalette } from './buildTypeColumnProps';
import { buildPooledTypeColumnProps } from './buildPooledTypeColumnProps';
import { buildSummaryColumnProps } from './buildSummaryColumnProps';
import { buildPoolColumnProps } from './buildPoolColumnProps';
import type { InventoryColumn } from './useTicketInventoryData';
import styles from './PunchupTicketInventory.module.css';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type Props = Pick<ChartPalette, 'ticketTypeColors' | 'poolColors' | 'onHoldColor' | 'compedColor'>;

const PunchupTicketInventory: FC<Props> = ({ ticketTypeColors, poolColors, onHoldColor, compedColor }) => {
	const { t } = useTranslation();
	const { id } = useParams<IdParamsType>();
	const compedByType = useCompedInventory(id);
	const { columns, summary, pools, hasPools, isSeated, isLoading } = useTicketInventoryData(compedByType);

	// Two independent wrapping grids: the pool grid (pools mode only) and the
	// type-card grid (summary + one card per ticket type). Each draws its own
	// wrap separator, so a pool card never shifts the type-card indices.
	const { ref: poolGridRef, newRowIndices: poolNewRows } = useNewRowIndices(hasPools ? pools.length : 0);
	const { ref: typeGridRef, newRowIndices: typeNewRows } = useNewRowIndices(1 + columns.length);

	if (!isLoading && summary.totalCapacity === 0) {
		return (
			<PunchupAnalyticsSection
				title={t('analytics-inventory-title')}
				description={t('analytics-inventory-description')}
			>
				<PunchupAnalyticsEmptyState>{t('analytics-inventory-empty')}</PunchupAnalyticsEmptyState>
			</PunchupAnalyticsSection>
		);
	}

	const palette: ColumnPalette = { ticketTypeColors, poolColors, onHoldColor, compedColor };

	// Pooled tiers route to a dedicated builder (dual-capacity tooltip, no pool wedge); standalone tiers
	// use the base builder. The pooled builder reads the pool's Lifetime-Σ usage straight off the
	// CapacityPool (single source) — no local recompute.
	const poolById = new Map(pools.map((p) => [p.poolId, p]));
	const buildTypeCard = (col: InventoryColumn): InventoryColumnCardProps => {
		const pool = col.poolId != null ? poolById.get(col.poolId) : undefined;
		return pool ? buildPooledTypeColumnProps(col, pool, palette, t) : buildTypeColumnProps(col, palette, t);
	};

	// Type-card grid: summary ("All Ticket Types") first, then one card per ticket type.
	// Pools are NOT here — they render in their own section above (when present).
	const typeCards: { key: string; props: InventoryColumnCardProps }[] = [
		{ key: 'summary', props: buildSummaryColumnProps(columns, summary, palette, t) },
		...columns.map((col) => ({ key: `type-${col.typeId}`, props: buildTypeCard(col) })),
	];

	return (
		<>
			{hasPools && (
				<PunchupAnalyticsSection
					title={t('analytics-pool-inventory-title')}
					description={t('analytics-pool-inventory-description')}
					isLoading={isLoading}
				>
					<div ref={poolGridRef} className={styles.columnGrid} data-cy="ticket-pool-inv-graph">
						{pools.map((p, i) => (
							<InventoryColumnCard
								key={`pool-${p.poolId}`}
								{...buildPoolColumnProps(p, columns, palette, t)}
								newRow={poolNewRows.has(i)}
							/>
						))}
					</div>
				</PunchupAnalyticsSection>
			)}

			<PunchupAnalyticsSection
				title={t('analytics-inventory-title')}
				description={t(isSeated ? 'analytics-inventory-description-seated' : 'analytics-inventory-description')}
				isLoading={isLoading}
			>
				<div ref={typeGridRef} className={styles.columnGrid} data-cy="ticket-inv-graph">
					{typeCards.map(({ key, props }, i) => (
						<InventoryColumnCard key={key} {...props} newRow={typeNewRows.has(i)} />
					))}
				</div>
			</PunchupAnalyticsSection>
		</>
	);
};

export default PunchupTicketInventory;
